import { Link, createFileRoute, useRouter } from '@tanstack/react-router'
import { Badge, Button, Card, TextField as Input } from '@react-spectrum/s2'
import {
  CheckCircle2,
  ExternalLink,
  Search,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'
import { getAdminFeedback, updateAdminFeedbackStatus } from '@rpc/admin.rpc'
import type { FormEvent } from 'react'
import { fullWidthStyles, statusBadgeStyles } from '@/styles/s2-macros'

const adminFeedbackSearchSchema = z.object({
  query: z.string().optional(),
  rating: z.enum(['positive', 'negative', 'all']).optional(),
  status: z.enum(['new', 'reviewed', 'actioned', 'all']).optional(),
})

const reasonLabels: Record<string, string> = {
  sources_useful: 'Cited sources were useful',
  matched_context: 'Matched project context',
  clear_practical: 'Clear and practical',
  helped_next_step: 'Helped next step',
  seems_accurate: 'Information seemed accurate',
  missing_guidance: 'Missing relevant guidance',
  unsupported_answer: 'Source did not support answer',
  too_vague: 'Too vague',
  misunderstood_context: 'Context misunderstood',
  outdated: 'Information seems outdated',
  hard_to_understand: 'Hard to understand',
  other: 'Other',
}

export const Route = createFileRoute('/admin/feedback')({
  validateSearch: (search) => adminFeedbackSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({
    query: search.query,
    rating: search.rating ?? 'all',
    status: search.status ?? 'new',
  }),
  loader: async ({ deps }) => {
    return await getAdminFeedback({
      data: {
        query: deps.query,
        rating: deps.rating,
        status: deps.status,
      },
    })
  },
  component: AdminFeedbackPage,
})

function AdminFeedbackPage() {
  const feedbackData = Route.useLoaderData()
  const routeSearch = Route.useSearch()
  const router = useRouter()
  const [query, setQuery] = useState(routeSearch.query ?? '')
  const [updatingId, setUpdatingId] = useState<string | null>(null)

  const navigateWithFilters = (
    filters: Partial<z.infer<typeof adminFeedbackSearchSchema>>,
  ) => {
    router.navigate({
      to: '/admin/feedback',
      search: {
        query: filters.query ?? routeSearch.query,
        rating: filters.rating ?? routeSearch.rating ?? 'all',
        status: filters.status ?? routeSearch.status ?? 'new',
      },
      replace: true,
    })
  }

  const handleSearch = (event: FormEvent) => {
    event.preventDefault()
    navigateWithFilters({ query: query.trim() || undefined })
  }

  const handleStatusUpdate = async (
    feedbackId: string,
    status: 'new' | 'reviewed' | 'actioned',
  ) => {
    setUpdatingId(feedbackId)
    try {
      await updateAdminFeedbackStatus({
        data: { feedbackId, status },
      })
      router.invalidate()
    } finally {
      setUpdatingId(null)
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
            Answer feedback
          </h2>
          <p className="text-neutral-500">
            Review per-response feedback and open the source conversation when
            an answer needs follow-up.
          </p>
        </div>
        <form
          onSubmit={handleSearch}
          className="grid gap-2 sm:grid-cols-[minmax(16rem,1fr)_auto_auto_auto]"
        >
          <Input
            aria-label="Search feedback"
            placeholder="Search feedback, answer, user, or conversation ID..."
            value={query}
            onChange={setQuery}
          />
          <select
            aria-label="Rating filter"
            value={routeSearch.rating ?? 'all'}
            onChange={(event) =>
              navigateWithFilters({
                rating: event.currentTarget.value as
                  | 'positive'
                  | 'negative'
                  | 'all',
              })
            }
            className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-800 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
          >
            <option value="all">All ratings</option>
            <option value="positive">Positive</option>
            <option value="negative">Negative</option>
          </select>
          <select
            aria-label="Status filter"
            value={routeSearch.status ?? 'new'}
            onChange={(event) =>
              navigateWithFilters({
                status: event.currentTarget.value as
                  | 'new'
                  | 'reviewed'
                  | 'actioned'
                  | 'all',
              })
            }
            className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-800 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
          >
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="actioned">Actioned</option>
            <option value="all">All statuses</option>
          </select>
          <Button
            type="submit"
            size="S"
            variant="secondary"
            fillStyle="outline"
            aria-label="Submit feedback search"
          >
            <Search className="h-4 w-4" />
          </Button>
        </form>
      </div>

      <Card styles={fullWidthStyles}>
        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {feedbackData.feedback.map((row) => (
            <article
              key={row.id}
              className="grid gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_16rem]"
            >
              <div className="min-w-0 space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant={
                      row.rating === 'positive' ? 'positive' : 'negative'
                    }
                    fillStyle="subtle"
                    styles={statusBadgeStyles}
                  >
                    {row.rating === 'positive' ? (
                      <ThumbsUp className="h-3.5 w-3.5" />
                    ) : (
                      <ThumbsDown className="h-3.5 w-3.5" />
                    )}
                    {row.rating}
                  </Badge>
                  <Badge
                    variant="neutral"
                    fillStyle="outline"
                    styles={statusBadgeStyles}
                  >
                    {row.status}
                  </Badge>
                  <span className="text-xs text-neutral-500">
                    {formatDateTime(row.feedbackCreatedAt)}
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                    Previous prompt
                  </div>
                  <p className="line-clamp-3 text-sm text-neutral-800 dark:text-neutral-200">
                    {row.previousUserPrompt || 'No user prompt found.'}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                    AI response
                  </div>
                  <p className="line-clamp-4 text-sm text-neutral-700 dark:text-neutral-300">
                    {row.messageContent}
                  </p>
                </div>

                {row.reasonTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {row.reasonTags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-700 dark:bg-neutral-900 dark:text-neutral-200"
                      >
                        {reasonLabels[tag] ?? tag}
                      </span>
                    ))}
                  </div>
                ) : null}

                {row.note ? (
                  <blockquote className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-800 dark:border-neutral-800 dark:bg-neutral-900/70 dark:text-neutral-200">
                    {row.note}
                  </blockquote>
                ) : null}
              </div>

              <aside className="space-y-4 text-sm">
                <div>
                  <div className="font-medium text-neutral-900 dark:text-neutral-100">
                    {formatOwnerName(row)}
                  </div>
                  <div className="text-xs text-neutral-500">
                    {formatOwnerDetail(row)}
                  </div>
                  <div className="mt-2 max-w-full truncate font-mono text-[11px] text-neutral-400">
                    {row.conversationId}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 lg:flex-col">
                  <Link
                    to="/admin/conversations"
                    search={{
                      conversationId: row.conversationId,
                    }}
                    className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-neutral-200 px-3 text-xs font-medium text-neutral-700 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 dark:border-neutral-800 dark:text-neutral-200 dark:hover:border-blue-900 dark:hover:bg-blue-950/40"
                  >
                    Open conversation
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                  {row.status !== 'reviewed' ? (
                    <Button
                      size="S"
                      variant="secondary"
                      fillStyle="outline"
                      onPress={() => handleStatusUpdate(row.id, 'reviewed')}
                      isDisabled={updatingId === row.id}
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      Mark reviewed
                    </Button>
                  ) : null}
                  {row.status !== 'actioned' ? (
                    <Button
                      size="S"
                      variant="secondary"
                      onPress={() => handleStatusUpdate(row.id, 'actioned')}
                      isDisabled={updatingId === row.id}
                    >
                      Mark actioned
                    </Button>
                  ) : null}
                </div>
              </aside>
            </article>
          ))}
          {feedbackData.feedback.length === 0 ? (
            <div className="px-6 py-14 text-center text-neutral-500">
              No answer feedback found.
            </div>
          ) : null}
        </div>
        <div className="flex items-center justify-between border-t border-neutral-200 bg-neutral-50/80 p-4 text-xs text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900/70">
          <span>Total feedback: {feedbackData.total}</span>
          <span>Showing latest {feedbackData.feedback.length}</span>
        </div>
      </Card>
    </div>
  )
}

function formatDateTime(value?: string | Date | null) {
  if (!value) return 'Unknown'
  return new Date(value).toLocaleString()
}

function formatOwnerName(row: {
  userName?: string | null
  userEmail?: string | null
  anonymousId?: string | null
}) {
  if (row.userName) return row.userName
  if (row.userEmail) return row.userEmail
  if (row.anonymousId) return 'Anonymous user'
  return 'Unknown user'
}

function formatOwnerDetail(row: {
  userEmail?: string | null
  anonymousId?: string | null
}) {
  if (row.userEmail) return row.userEmail
  if (row.anonymousId) return row.anonymousId
  return 'No owner detail'
}
